import { useState } from "react";
import { Check, CreditCard, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { pay, errMsg } from "../../lib/api";
import { PageHeader, Panel, Loader, ErrorNote, Btn, shortDate } from "../../components/app/kit";
import { BrandLine } from "../../components/Brand";

const PERKS = ["Search every GAMEDAY player", "Saved board with tags and private notes", "Message coaches directly", "Branded prospect cards with highlights"];
const money = (cents, cur) => new Intl.NumberFormat(undefined, { style: "currency", currency: (cur || "usd").toUpperCase() }).format((cents ?? 0) / 100);

export const useRecruiterPlan = () => useApi("/recruiter/me", pay);

export default function RecruiterPlan({ sub: given }) {
  const own = useRecruiterPlan();
  const sub = given ?? own;
  const plan = useApi("/plan", pay);
  const [busy, setBusy] = useState(false);
  const s = sub?.data;
  const go = async (path) => {
    setBusy(true);
    try {
      const { data } = await pay.post(path, { origin_url: window.location.origin });
      window.location.href = data.checkout_url ?? data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  if ((!sub?.data && !sub?.error) || plan.loading) return <Loader />;
  if (sub?.error) return <ErrorNote error={sub.error} />;
  const p = plan.data ?? {};
  const trial = s?.trial_available !== false;
  return (
    <div>
      <PageHeader eyebrow="Recruiter plan" title={s?.active ? "Subscription" : "Unlock recruiting"} />
      <BrandLine className="-mt-4 mb-6" testid="plan-brand-line" />
      <Panel className="max-w-2xl">
        {s?.active ? (
          <div data-testid="recruiter-plan-active" className="space-y-4">
            <p className="inline-flex items-center gap-2 rounded-full bg-[#39FF14]/15 px-4 py-1.5 font-mono-gd text-xs uppercase tracking-[0.2em] text-[#39FF14]">
              <Check className="h-4 w-4" />{s.status === "admin" ? "Admin access" : s.status === "trialing" ? "Free trial" : s.status}
            </p>
            {s.since && <p className="text-slate-300">Active since {shortDate(s.since)}.</p>}
            {s.has_portal && <Btn variant="ghost" disabled={busy} data-testid="recruiter-manage-billing" onClick={() => go("/recruiter/portal")}><CreditCard className="h-4 w-4" />Manage billing</Btn>}
          </div>
        ) : (
          <div data-testid="recruiter-paywall" className="space-y-6">
            <div>
              <p className="font-display text-6xl font-black text-white">{money(p.amount, p.currency)}<span className="ml-2 text-lg font-semibold text-slate-400">/ {p.interval ?? "month"}</span></p>
              <p data-testid="recruiter-trial-note" className="mt-2 text-slate-300">{trial ? `Free for ${p.trial_days ?? 14} days, then billed monthly. Cancel any time.` : "Your free trial has been used. Subscribe to keep recruiting."}</p>
            </div>
            <ul className="space-y-2">{PERKS.map((t) => <li key={t} className="flex items-center gap-3 text-slate-200"><Check className="h-4 w-4 text-[#39FF14]" />{t}</li>)}</ul>
            <Btn disabled={busy} data-testid="recruiter-start-trial" onClick={() => go("/recruiter/checkout")} className="px-8 py-3.5 text-base">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{trial ? "Start your free trial" : "Subscribe"}
            </Btn>
            {s?.status && !["none"].includes(s.status) && <p className="text-xs text-slate-400">Last status: {s.status}</p>}
          </div>
        )}
      </Panel>
    </div>
  );
}
